import { isDevEnv } from '@flow/env';
import { rollbarReporter } from './rollbar/reporter';
import { clientLogger } from './client-logger';
import type {
  ClientLogLevel,
  ErrorMessage,
  ErrorReporterLogLevel,
  LogData,
  LogMessage,
} from './types';

const mapReporterToClientLogLevel = (level: ErrorReporterLogLevel): ClientLogLevel => {
  if (level === 'critical') {
    return 'error';
  }
  return level as ClientLogLevel; // 'debug' and 'info' map directly
};

type LoggerFunction = (message: ErrorMessage | LogMessage, data?: LogData) => void;

const memoizedReporters: Partial<Record<ErrorReporterLogLevel, LoggerFunction>> = {};

/**
 * Creates a reporter function for the specified report level.
 *
 * In production environments, the reporter function report to Rollbar
 * after stripping sensitive text content from the message.
 *
 * @param level - The report level to use for the reporter (e.g., "info", "error").
 * @returns A reporter function that takes a message and optional data. In production,
 *          it reports the message to Rollbar. In non-production, it does nothing.
 */
const createReporter = (level: ErrorReporterLogLevel): LoggerFunction => {
  return (message, data) => {
    if (!isDevEnv()) {
      rollbarReporter[level](message, data);
    }
    const clientLogLevel = mapReporterToClientLogLevel(level);
    clientLogger[clientLogLevel](`ErrorReporter: ${message}`, data);
  };
};

const getReporter = (level: ErrorReporterLogLevel): LoggerFunction => {
  if (!memoizedReporters[level]) {
    memoizedReporters[level] = createReporter(level);
  }
  return memoizedReporters[level]!;
};

export const errorReporter: Record<ErrorReporterLogLevel, LoggerFunction> = {
  critical: getReporter('critical'),
  error: getReporter('error'),
};
